import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LoadingIcon, TopDropdown } from "../../../components";
import { getGreenhouses } from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import { useLocalStorage } from "../../../hooks";

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
  greenHouseLabel,
  setGreenHouseLabel,
  blockLabel,
  setBlockLabel,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [plotLoading, setPlotLoading] = useState(false);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const getPlotDates = (plot) => (plot?.harvests || []).map((d) => d.date);

  const findPlot = (plotId, greenhouse = currentGreenhouse) =>
    (greenhouse?.plots || plotData || []).find(
      (plot) => plot.id === plotId && (!plot.greenhouse_id || plot.greenhouse_id === greenhouse?.id),
    ) ||
    (greenhouse?.plots || plotData || []).find((plot) => plot.id === plotId);

  const syncPlotSelection = (plot, greenhouse = currentGreenhouse, preferredDate = null) => {
    if (!plot) return;
    const dates = getPlotDates(plot);
    const nextDate = preferredDate && dates.includes(preferredDate) ? preferredDate : dates[0] || null;

    setCurrentPlot(plot);
    setDateData(dates);
    setCurrentDate(nextDate);

    const params = new URLSearchParams(searchParams);
    if (greenhouse) params.set("greenhouse", greenhouse.id);
    params.set("plot", plot.id);
    if (nextDate) params.set("date", nextDate);
    else params.delete("date");
    setSearchParams(params);

    const orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        greenhouse: greenhouse?.id || orgObj.greenhouse,
        plot: plot.id,
        date: nextDate || orgObj.date,
      },
    });
  };

  const numericFamilyCompare = (aStr, bStr) => {
    const parse = (s) => {
      const str = (s ?? "").toString();
      const match = str.match(/^(\d+)/);
      const num = match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
      const rest = str.slice(match ? match[1].length : 0).toLowerCase();

      const rank =
        rest === ""
          ? 0
          : rest.includes("new")
            ? 1
            : rest.includes("old")
              ? 2
              : 3;

      return { num, rank, str };
    };

    const A = parse(aStr);
    const B = parse(bStr);

    if (A.num !== B.num) return A.num - B.num; // numeric part
    if (A.rank !== B.rank) return A.rank - B.rank; // suffix priority
    return A.str.localeCompare(B.str, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    try {
      const res = await getGreenhouses(orgId, siteId, cropId);
      const greenhouseList = Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      const data = greenhouseList.map((greenhouse) => ({
        name: greenhouse.name,
        id: greenhouse.id,
        plots: (greenhouse.plots || []).map((plot) => ({
          ...plot,
          greenhouse_id: greenhouse.id,
          greenhouse_name: greenhouse.name,
        })),
        metadata: greenhouse.metadata,
      }));
      const sortedData = [...data].sort((a, b) =>
        numericFamilyCompare(a.name, b.name),
      );
      setGreenhouseData(sortedData);
    } catch (err) {
      // handle errors if needed
    } finally {
      setGreenhouseLoading(false);
    }
  };

  const disabledDate = (current) => {
    const dayjsDates = dateData.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((isEnabled, date) => {
        if (isEnabled) {
          return true;
        } else {
          return date.isSame(current, "day");
        }
      }, false)
    );
  };

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      setCurrentDate(dateString);
      const params = new URLSearchParams(searchParams);
      params.set("date", dateString);
      setSearchParams(params);
    }
  };

  // Whenever user picks site/crop, fetch greenhouse
  useEffect(() => {
    if (currentSite) {
      const sm = currentSite.metadata[currentCrop?.name] || {};
      setSiteMetadata(sm);

      if (sm.greenhouse_name) {
        setGreenHouseLabel(sm.greenhouse_name);
      }
      if (sm.block_name) {
        setBlockLabel(sm.block_name);
      }

      fetchGreenhouses(currentOrg, currentSite.id, currentCrop?.id);
    }
  }, [currentSite, currentCrop]);

  // Once greenhouseData is loaded, pick default greenhouse
  useEffect(() => {
    if (!greenhouseData || greenhouseData.length === 0) {
      setCurrentGreenhouse(null);
      setPlotData([]);
      return;
    }
    let params = new URLSearchParams(searchParams);
    let greenhouseIdFromUrlParams = params.get("greenhouse");
    let chosen = null;

    if (greenhouseIdFromUrlParams) {
      chosen = greenhouseData.find(
        (item) => item.id === greenhouseIdFromUrlParams,
      );
    } else if (!settingDefaults.greenhouse) {
      const defGHId = defaultDropdown?.[currentOrg]?.greenhouse;
      if (defGHId) {
        chosen = greenhouseData.find((gh) => gh.id === defGHId);
      }
      // fallback
      if (!chosen) chosen = greenhouseData[0];
      setSettingDefaults((prev) => ({ ...prev, greenhouse: true }));
    } else {
      chosen = greenhouseData[0];
    }

    if (!chosen) {
      chosen = greenhouseData[0];
    }
    setCurrentGreenhouse(chosen);
    setPlotData(chosen.plots || []);
  }, [greenhouseData]);

  // Whenever greenhouse changes, set plot data
  useEffect(() => {
    if (currentGreenhouse?.plots) {
      setPlotData(currentGreenhouse.plots);
    } else {
      setPlotData([]);
    }
  }, [currentGreenhouse]);

  // Once plotData is set, pick a default plot
  useEffect(() => {
    if (!plotData || plotData.length === 0) {
      setCurrentPlot(null);
      setDateData([]);
      return;
    }
    let params = new URLSearchParams(searchParams);
    let plotIdFromUrlParams = params.get("plot");
    let dateFromUrlParams = params.get("date");
    let chosen = null;

    if (plotIdFromUrlParams) {
      chosen = findPlot(plotIdFromUrlParams);
    } else if (!settingDefaults.plot) {
      const defPlotId = defaultDropdown?.[currentOrg]?.plot;
      if (defPlotId) {
        chosen = findPlot(defPlotId);
      }
      // fallback
      if (!chosen) chosen = plotData[0];
      setSettingDefaults((prev) => ({ ...prev, plot: true }));
    } else {
      chosen = plotData[0];
    }
    if (!chosen) {
      chosen = plotData[0];
    }
    syncPlotSelection(
      chosen,
      currentGreenhouse,
      dateFromUrlParams || defaultDropdown?.[currentOrg]?.date,
    );
  }, [plotData]);

  // Once we have a currentPlot, set dateData
  useEffect(() => {
    if (currentPlot?.harvests) {
      setDateData(getPlotDates(currentPlot));
    } else {
      setDateData([]);
    }
  }, [currentPlot]);

  // Once dateData is set, pick a default date
  useEffect(() => {
    if (!dateData || dateData.length === 0) {
      setCurrentDate(null);
      return;
    }
    let params = new URLSearchParams(searchParams);
    let dateFromUrl = params.get("date");
    let chosen = null;

    if (dateFromUrl && dateData.includes(dateFromUrl)) {
      chosen = dateFromUrl;
    } else {
      chosen = dateData[0];
    }

    setCurrentDate(chosen);
  }, [dateData]);

  // Whenever current greenhouse changes, set it in query params + localStorage (ID only)
  useEffect(() => {
    if (currentGreenhouse) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGreenhouse]);

  // Whenever current plot changes, set it in query params + localStorage (ID only)
  useEffect(() => {
    if (currentPlot) {
      let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // Set loading state for all dropdowns
  useEffect(() => {
    setDropdownLoading(
      orgLoading ||
        siteLoading ||
        cropLoading ||
        plotLoading ||
        greenhouseLoading ||
        !currentDate,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    plotLoading,
    greenhouseLoading,
    currentDate,
    setDropdownLoading,
  ]);

  return (
    <>
      {orgLoading || siteLoading || plotLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4 !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            val={currentGreenhouse?.id ?? undefined}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(id) => {
              const greenhouseObj = greenhouseData.find((gh) => gh.id === id);
              setCurrentGreenhouse(greenhouseObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!greenhouseData) return [];
              const sortedGH = [...greenhouseData].sort((a, b) =>
                numericFamilyCompare(a.name, b.name),
              );
              return sortedGH.map((greenhouse) => ({
                label: greenhouse.name,
                value: greenhouse.id,
              }));
            })()}
          />

          <TopDropdown
            label={`Select ${blockLabel}:`}
            val={
              currentGreenhouse && currentPlot
                ? `${currentGreenhouse.id}---${currentPlot.id}`
                : undefined
            }
            placeholder={`Select ${blockLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(value) => {
              const [greenhouseId, plotId] = value.split("---");
              const greenhouse = greenhouseData.find((gh) => gh.id === greenhouseId);
              syncPlotSelection(findPlot(plotId, greenhouse), greenhouse);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!plotData) return [];
              const sortedPlot = [...plotData].sort((a, b) =>
                numericFamilyCompare(a.name, b.name),
              );
              return sortedPlot.map((plot) => ({
                label: plot.name,
                value: `${plot.greenhouse_id || currentGreenhouse?.id}---${plot.id}`,
              }));
            })()}
          />

          {/* If you need a separate date picker, you can keep this or remove it:
          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">Select Date:</div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="max-w-32 min-w-[150px] sm:mb-5"
              disabledDate={disabledDate}
              onChange={handleDateChange}
            />
          </div>
          */}
        </div>
      )}
    </>
  );
};

export default Dropdowns;
